import type { Branch } from "../../branch";
import type { AgrianType, Company, portalConfigurationOption } from "../types";

/** True if any of the company's `portalConfigurationOptions` contains `optionSubstring` (case-insensitive). */
export function configContains(company: Company, optionSubstring: string): boolean {
	return (
		company.portalConfigurationOptions.filter((option) => option.toLowerCase().includes(optionSubstring.toLowerCase()))
			.length > 0
	);
}

/** True if the company has the Proagrica import flow enabled. */
export function hasProagrica(company: Company): boolean {
	return configContains(company, "proagrica");
}

/**
 * True if the company has Agrian enabled in portal config AND either the company itself
 * or any selected branch carries at least one Agrian Org ID (`externalLocationId`).
 */
export function hasAgrian(company: Company, branches: Branch[]): boolean {
	const companyHasAgrianOrgID = (company?.externalLocationId?.length ?? 0) > 0;
	const branchHasAgrianOrgID = branches.some((branch) => (branch?.externalLocationId?.length ?? 0) > 0);
	const hasAgrianOrgID = companyHasAgrianOrgID || branchHasAgrianOrgID;

	return configContains(company, "agrian") && hasAgrianOrgID;
}

/**
 * True if the company imports only from pre-existing Agrian sample events.
 *
 * Distinct from the `configContains` flags above: those read the
 * `portalConfigurationOptions` multi-select (which import flows are *enabled*), whereas this
 * reads the `portalSettings` JSON blob (how an enabled flow *behaves*). The single reader keeps
 * that optional-chaining depth in one place.
 */
export function isPreExistingEventsOnly(company?: Company | null): boolean {
	return company?.portalSettings?.agrian?.preExistingEventsOnly ?? false;
}

/** The two mutually-exclusive Agrian import-flow option keys. A company is
 *  Echelon or Agellum — never both. */
export const AGRIAN_FLOW_OPTIONS = {
	Echelon: "Import: Agrian (Echelon)",
	Agellum: "Import: Agrian (Agellum)",
} as const satisfies Record<AgrianType, portalConfigurationOption>;

/** Apply the Echelon/Agellum exclusivity rule to an import-flow settings map.
 *  Enabling one Agrian flow disables the other; all non-Agrian keys pass through. */
export function applyAgrianExclusivity(
	settings: Record<string, boolean>,
	changedKey: string,
	checked: boolean,
): Record<string, boolean> {
	const next = { ...settings, [changedKey]: checked };

	if (checked && changedKey === AGRIAN_FLOW_OPTIONS.Echelon) {
		next[AGRIAN_FLOW_OPTIONS.Agellum] = false;
	} else if (checked && changedKey === AGRIAN_FLOW_OPTIONS.Agellum) {
		next[AGRIAN_FLOW_OPTIONS.Echelon] = false;
	}

	return next;
}

/** The AgrianType implied by an import-flow settings map, or null when no
 *  Agrian flow is enabled. */
export function agrianTypeFromFlowSettings(settings: Record<string, boolean>): AgrianType | null {
	if (settings[AGRIAN_FLOW_OPTIONS.Echelon]) return "Echelon";
	if (settings[AGRIAN_FLOW_OPTIONS.Agellum]) return "Agellum";
	return null;
}
